/* eslint-disable react/display-name */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react"
import { Field } from "react-final-form"

import { ClickOutside, InputControl } from "@trueskin-web/components"
import i18next from "@trueskin-web/translations"

type EmailAutocompleteProps = {
  domains: string[]
  as?: React.ComponentType<any>
  name?: string
  label?: string
  isDisabled?: boolean
  desktopErrorMessagePlaceholder?: boolean
  validate?: () => void
  onInputChange?: (value: string) => void
}

type SuggestionsListProps = {
  suggestions: string[]
  email: string
  focusedIndex: number
  onClick: (value: string) => void
}

const EmailAutocomplete = forwardRef<HTMLInputElement, EmailAutocompleteProps>(
  (
    {
      domains = [],
      as: Input = InputControl,
      name = "email",
      label = i18next.t("Start.CreateAccount.email"),
      isDisabled = false,
      desktopErrorMessagePlaceholder,
      validate,
      onInputChange,
    },
    ref
  ) => {
    const [email, setEmail] = useState<string>("")
    const [showSuggestions, setShowSuggestions] = useState<boolean>(false)
    const [filteredDomains, setFilteredDomains] = useState<string[]>([])
    const [focusedIndex, setFocusedIndex] = useState<number>(-1)

    const searchMatchingDomains = (value: string) => {
      const atIndex = value.indexOf("@")

      if (atIndex > -1 && value.split("@")[1].length > 0) {
        const typedDomain = value.slice(atIndex + 1)
        const matchingDomains = domains.filter((domain: string) =>
          domain.startsWith(typedDomain)
        )

        setFilteredDomains(matchingDomains)
        setShowSuggestions(true)
      } else {
        setShowSuggestions(false)
        setFocusedIndex(-1)
      }
    }

    const handleInputChage = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value

      onInputChange?.(value)
      setEmail(value)
      searchMatchingDomains(value)
    }

    const handleSuggestionClick = (domain: string) => {
      const atIndex = email.indexOf("@")
      const completedEmail = email.slice(0, atIndex + 1) + domain

      setEmail(completedEmail)
      setShowSuggestions(false)
      onInputChange?.(completedEmail)
    }

    const handleInputFocus = () => {
      searchMatchingDomains(email)
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!showSuggestions || filteredDomains.length === 0) return

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault()
          setFocusedIndex((prev) =>
            prev < filteredDomains.length - 1 ? prev + 1 : prev
          )
          break
        case "ArrowUp":
          e.preventDefault()
          setFocusedIndex((prev) => (prev > 0 ? prev - 1 : prev))
          break
        case "Enter":
          e.preventDefault()
          if (focusedIndex >= 0) {
            handleSuggestionClick(filteredDomains[focusedIndex])
            setFocusedIndex(-1)
          }
          break
        case "Escape":
          setShowSuggestions(false)
          setFocusedIndex(-1)
          break
        default:
          break
      }
    }

    const handleClickOutside = useCallback(() => {
      setShowSuggestions(false)
      setFocusedIndex(-1)
    }, [])

    return (
      <div
        sx={{
          position: "relative",
        }}
      >
        <ClickOutside onClickOutside={handleClickOutside}>
          <Field
            data-testid="web-portal-create-account-email"
            ref={ref}
            name={name}
            component={Input}
            desktopErrorMessagePlaceholder={desktopErrorMessagePlaceholder}
            validate={validate}
            onChange={handleInputChage}
            onFocus={handleInputFocus}
            onKeyDown={handleKeyDown}
            type="email"
            label={label}
          />

          {!isDisabled && showSuggestions && filteredDomains.length > 0 && (
            <SuggestionsList
              suggestions={filteredDomains}
              email={email}
              focusedIndex={focusedIndex}
              onClick={handleSuggestionClick}
            />
          )}
        </ClickOutside>
      </div>
    )
  }
)

const SuggestionsList: React.FC<SuggestionsListProps> = ({
  suggestions,
  email,
  focusedIndex,
  onClick,
}) => {
  const listItemRefs = useRef<(HTMLLIElement | null)[]>([])

  useEffect(() => {
    if (focusedIndex !== -1 && listItemRefs.current[focusedIndex]) {
      listItemRefs.current[focusedIndex]?.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      })
    }
  }, [focusedIndex])

  return (
    <ul
      data-testid="web-portal-create-account-suggestions-list"
      sx={{
        position: "absolute",
        top: 70,
        left: 0,
        width: "100%",
        maxHeight: "200px",
        overflowY: "auto",
        backgroundColor: "background",
        border: "1px",
        borderColor: "grey",
        listStyleType: "none",
        p: 0,
        m: 0,
        zIndex: 1,
      }}
    >
      {suggestions.map((suggestion: string, index: number) => (
        <li
          key={index}
          ref={(el) => (listItemRefs.current[index] = el)}
          data-testid={`web-portal-create-account-suggestions-list_${suggestion}`}
          onClick={() => onClick(suggestion)}
          sx={{
            p: 2,
            backgroundColor:
              focusedIndex === index ? "neutralsLightGrey" : "background",
            cursor: "pointer",
            transition: "all .2s ease-in ",
            "&:hover, &:focus": {
              backgroundColor: "neutralsLightGrey",
            },
          }}
        >
          {email.slice(0, email.indexOf("@") + 1)}
          <span sx={{ fontWeight: "bold" }}>{suggestion}</span>
        </li>
      ))}
    </ul>
  )
}

export default EmailAutocomplete
